import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from "react";
import "./styles.css";
import {
  TD3_ACTIVE_STEPS_MAX,
  TD3_ACTIVE_STEPS_MIN,
  TD3_PLAYABLE_PITCH_MAX,
  TD3_PLAYABLE_PITCH_MIN,
  allLocations,
  buildPatternRequestSysEx,
  buildPatternSysEx,
  concatSysex,
  decodePatternDump,
  decodePatternDumpFile,
  isPatternDump,
  locationLabel,
  locationToIndex,
  patternSummary,
  pitchName,
  rangeLocations,
  type PatternDump,
  type Td3Location,
  type Td3Pattern,
} from "./td3Protocol";
import {
  PRESETS,
  ROOT_NAMES,
  generatePatterns,
  makeEntropySeed,
  normaliseSettings,
  type ChaosSettings,
  type ScaleId,
} from "./randomizer";
import {
  commitRecipeManualState,
  createRecipeManualHistory,
  redoRecipeManualState,
  undoRecipeManualState,
  withManualSetting,
  withPitchRange,
  withPresetSelection,
  withRecipeShape,
  withLocalRecipeSelection,
  withoutLocalRecipeLink,
  type RecipeManualState,
  type RecipePresetId,
} from "./recipeManualHistory";
import {
  LOCAL_RECIPES_LIMIT,
  createLocalRecipeFromState,
  loadLocalRecipes,
  localRecipeMatchesState,
  saveLocalRecipes,
  suggestLocalRecipeName,
  type LocalRecipe,
} from "./localRecipes";

type TargetMode = "all" | "range" | "single";
type TabId = "recipes" | "manual" | "activity";
type LogLevel = "ok" | "warn" | "error" | "info";

interface LogEntry {
  id: number;
  level: LogLevel;
  text: string;
}

interface BatchItem {
  location: Td3Location;
  pattern: Td3Pattern;
  sysex: Uint8Array;
}

interface DumpMapEntry {
  id: number;
  startIndex: number;
  endIndex: number;
  recipeName: string;
  settings: ChaosSettings;
}

const LOCATION_OPTIONS = allLocations();
const sleep = (ms: number) =>
  new Promise((resolve) => window.setTimeout(resolve, ms));

function clampLocationIndex(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(LOCATION_OPTIONS.length - 1, Math.round(value)));
}

function locationRangeLabel(startIndex: number, endIndex: number): string {
  const start = LOCATION_OPTIONS[clampLocationIndex(startIndex)];
  const end = LOCATION_OPTIONS[clampLocationIndex(endIndex)];
  return startIndex === endIndex
    ? locationLabel(start)
    : `${locationLabel(start)} → ${locationLabel(end)}`;
}

function isTextEditingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLSelectElement) return true;
  if (!(target instanceof HTMLInputElement)) return false;

  return [
    "date",
    "datetime-local",
    "email",
    "month",
    "number",
    "password",
    "search",
    "tel",
    "text",
    "time",
    "url",
    "week",
  ].includes(target.type);
}
function recipeSettingsAreTweaked(
  settings: ChaosSettings,
  preset: (typeof PRESETS)[number] | undefined,
): boolean {
  if (!preset) return false;
  const baseline = normaliseSettings(preset.settings);
  return (
    settings.lengthMin !== baseline.lengthMin ||
    settings.lengthMax !== baseline.lengthMax ||
    settings.tripletChance !== baseline.tripletChance ||
    settings.accentChance !== baseline.accentChance ||
    settings.slideChance !== baseline.slideChance ||
    settings.tieChance !== baseline.tieChance ||
    settings.restChance !== baseline.restChance ||
    settings.pitchMin !== baseline.pitchMin ||
    settings.pitchMax !== baseline.pitchMax ||
    settings.scaleId !== baseline.scaleId ||
    settings.root !== baseline.root ||
    settings.enforceAudibleStep !== baseline.enforceAudibleStep ||
    settings.allowTieOnFirstStep !== baseline.allowTieOnFirstStep
  );
}

function App() {
  const [midiAccess, setMidiAccess] = useState<MIDIAccess | null>(null);
  const [inputs, setInputs] = useState<MIDIInput[]>([]);
  const [outputs, setOutputs] = useState<MIDIOutput[]>([]);
  const [selectedInputId, setSelectedInputId] = useState("");
  const [selectedOutputId, setSelectedOutputId] = useState("");
  const [recipeHistory, setRecipeHistory] = useState(() =>
    createRecipeManualHistory(),
  );
  const { settings, keepRecipeShape } = recipeHistory.present;
  const safeSettings = useMemo(() => normaliseSettings(settings), [settings]);
  const canUndoRecipe = recipeHistory.past.length > 0;
  const canRedoRecipe = recipeHistory.future.length > 0;
  const [localRecipes, setLocalRecipes] = useState<LocalRecipe[]>(() =>
    loadLocalRecipes(),
  );
  const [targetMode, setTargetMode] = useState<TargetMode>("all");
  const [activeTab, setActiveTab] = useState<TabId>("recipes");
  const [singleIndex, setSingleIndex] = useState(0);
  const [rangeStart, setRangeStart] = useState(0);
  const [rangeEnd, setRangeEnd] = useState(63);
  const [seed, setSeed] = useState("");
  const [sendDelayMs, setSendDelayMs] = useState(85);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [progress, setProgress] = useState<{
    done: number;
    total: number;
    action: string;
  } | null>(null);
  const [dumpMapEntries, setDumpMapEntries] = useState<DumpMapEntry[]>([]);
  const [isDumpMapOpen, setIsDumpMapOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [lastBatch, setLastBatch] = useState<BatchItem[]>([]);
  const [lastRunSeed, setLastRunSeed] = useState("");
  const restoreBackupInputRef = useRef<HTMLInputElement | null>(null);
  const nextDumpMapEntryId = useRef(1);
  const pendingDump = useRef<{
    expected: Td3Location;
    resolve: (dump: PatternDump) => void;
    reject: (error: Error) => void;
    timeoutId: number;
  } | null>(null);
  const cancelRequested = useRef(false);

  const selectedOutput =
    outputs.find((output) => output.id === selectedOutputId) ?? null;
  const selectedInput =
    inputs.find((input) => input.id === selectedInputId) ?? null;
  const isTd3Connected = Boolean(
    midiAccess?.sysexEnabled && selectedOutput && selectedInput,
  );

  const selectedLocations = useMemo(() => {
    if (targetMode === "all") return LOCATION_OPTIONS;
    if (targetMode === "single")
      return [LOCATION_OPTIONS[clampLocationIndex(singleIndex)]];
    return rangeLocations(
      LOCATION_OPTIONS[clampLocationIndex(rangeStart)],
      LOCATION_OPTIONS[clampLocationIndex(rangeEnd)],
    );
  }, [rangeEnd, rangeStart, singleIndex, targetMode]);

  const currentPreset = PRESETS.find(
    (preset) => preset.id === safeSettings.presetId,
  );
  const currentLocalRecipe = useMemo(
    () =>
      localRecipes.find(
        (recipe) => recipe.id === recipeHistory.present.localRecipeId,
      ) ?? null,
    [localRecipes, recipeHistory.present.localRecipeId],
  );
  const currentLocalRecipeIsTweaked = currentLocalRecipe
    ? !localRecipeMatchesState(currentLocalRecipe, recipeHistory.present)
    : false;
  const currentRecipeIsTweaked = recipeSettingsAreTweaked(
    safeSettings,
    currentPreset,
  );
  const currentRecipeName = currentLocalRecipe
    ? `${currentLocalRecipe.name}${currentLocalRecipeIsTweaked ? " + manual" : ""}`
    : currentPreset
    ? `${currentPreset.name}${currentRecipeIsTweaked ? " + manual" : ""}`
    : "Custom";
  const dumpMapStats = useMemo(() => {
    const owners = new Map<number, number>();
    let overlapCount = 0;
    dumpMapEntries.forEach((entry, entryIndex) => {
      for (let index = entry.startIndex; index <= entry.endIndex; index += 1) {
        if (owners.has(index)) overlapCount += 1;
        owners.set(index, entryIndex);
      }
    });
    return { slotCount: owners.size, overlapCount };
  }, [dumpMapEntries]);
  const hasDumpMap = dumpMapEntries.length > 0;
  const activeSlotCount = hasDumpMap
    ? dumpMapStats.slotCount
    : selectedLocations.length;
  const activeSlotLabel = hasDumpMap
    ? `${dumpMapEntries.length} mapped region${dumpMapEntries.length === 1 ? "" : "s"}`
    : locationRangeLabel(
        locationToIndex(selectedLocations[0]),
        locationToIndex(selectedLocations[selectedLocations.length - 1]),
      );

  const appendLog = (level: LogLevel, text: string) => {
    setLogs((existing) =>
      [{ id: Date.now() + Math.random(), level, text }, ...existing].slice(
        0,
        120,
      ),
    );
  };

  const refreshPorts = (access: MIDIAccess) => {
    const nextInputs = Array.from(access.inputs.values());
    const nextOutputs = Array.from(access.outputs.values());
    setInputs(nextInputs);
    setOutputs(nextOutputs);

    setSelectedInputId((current) => {
      if (current && nextInputs.some((input) => input.id === current))
        return current;
      return (
        nextInputs.find((input) => /td-?3|behringer/i.test(input.name ?? ""))
          ?.id ??
        nextInputs[0]?.id ??
        ""
      );
    });

    setSelectedOutputId((current) => {
      if (current && nextOutputs.some((output) => output.id === current))
        return current;
      return (
        nextOutputs.find((output) => /td-?3|behringer/i.test(output.name ?? ""))
          ?.id ??
        nextOutputs[0]?.id ??
        ""
      );
    });
  };

  const connectMidi = async () => {
    try {
      if (!navigator.requestMIDIAccess) {
        appendLog(
          "error",
          "WebMIDI is not available in this browser. Use Chrome, Edge or Opera over HTTPS/localhost.",
        );
        return;
      }
      const access = await navigator.requestMIDIAccess({ sysex: true });
      setMidiAccess(access);
      refreshPorts(access);
      access.onstatechange = () => refreshPorts(access);
      appendLog(
        access.sysexEnabled ? "ok" : "warn",
        access.sysexEnabled
          ? "MIDI connected with SysEx enabled."
          : "MIDI connected, but SysEx is not enabled. Pattern upload needs SysEx permission.",
      );
    } catch (error) {
      appendLog(
        "error",
        `MIDI permission failed: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  };

  useEffect(() => {
    if (!selectedInput) return;
    const handler = (event: MIDIMessageEvent) => {
      const data = event.data;
      if (!data || !isPatternDump(data)) return;
      const pending = pendingDump.current;
      if (!pending || !isPatternDump(data, pending.expected)) return;
      window.clearTimeout(pending.timeoutId);
      pendingDump.current = null;
      try {
        pending.resolve(decodePatternDump(data));
      } catch (error) {
        pending.reject(
          error instanceof Error ? error : new Error(String(error)),
        );
      }
    };
    selectedInput.addEventListener("midimessage", handler);
    return () => selectedInput.removeEventListener("midimessage", handler);
  }, [selectedInput]);

  useEffect(() => {
    if (!isAboutOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsAboutOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [isAboutOpen]);

  const commitRecipeEdit = (
    updater: (state: RecipeManualState) => RecipeManualState,
    groupKey?: string,
  ) => {
    setRecipeHistory((history) =>
      commitRecipeManualState(history, updater, { groupKey }),
    );
  };

  const undoRecipeEdit = () => {
    setRecipeHistory(undoRecipeManualState);
  };

  const redoRecipeEdit = () => {
    setRecipeHistory(redoRecipeManualState);
  };

  useEffect(() => {
    const handleHistoryShortcut = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey) return;
      if (isTextEditingTarget(event.target)) return;

      const key = event.key.toLowerCase();
      const wantsUndo = key === "z" && !event.shiftKey;
      const wantsRedo = key === "y" || (key === "z" && event.shiftKey);
      if (!wantsUndo && !wantsRedo) return;

      if (wantsUndo && canUndoRecipe) {
        event.preventDefault();
        undoRecipeEdit();
      }
      if (wantsRedo && canRedoRecipe) {
        event.preventDefault();
        redoRecipeEdit();
      }
    };

    window.addEventListener("keydown", handleHistoryShortcut);
    return () => window.removeEventListener("keydown", handleHistoryShortcut);
  }, [canRedoRecipe, canUndoRecipe]);

  const updateSetting = <K extends keyof ChaosSettings>(
    key: K,
    value: ChaosSettings[K],
  ) => {
    commitRecipeEdit(
      (current) => withManualSetting(current, key, value),
      `setting:${String(key)}`,
    );
  };

  const selectPreset = (presetId: RecipePresetId) => {
    commitRecipeEdit((current) => withPresetSelection(current, presetId));
    appendLog(
      "info",
      `Recipe loaded: ${PRESETS.find((preset) => preset.id === presetId)?.name ?? presetId}.`,
    );
  };

  const selectLocalRecipe = (recipe: LocalRecipe) => {
    commitRecipeEdit((current) => withLocalRecipeSelection(current, recipe));
    appendLog("info", `Local recipe loaded: ${recipe.name}.`);
  };

  const storeLocalRecipes = (recipes: LocalRecipe[]) => {
    const saved = saveLocalRecipes(recipes);
    if (!saved) {
      appendLog(
        "warn",
        "Local recipe storage is unavailable in this browser session.",
      );
    }
  };

  const addLocalRecipe = () => {
    const recipe = createLocalRecipeFromState(
      recipeHistory.present,
      suggestLocalRecipeName(localRecipes, currentRecipeName),
    );
    const nextLocalRecipes = [recipe, ...localRecipes].slice(
      0,
      LOCAL_RECIPES_LIMIT,
    );
    storeLocalRecipes(nextLocalRecipes);
    setLocalRecipes(nextLocalRecipes);
    commitRecipeEdit((current) => withLocalRecipeSelection(current, recipe));
    appendLog("ok", `Local recipe saved: ${recipe.name}.`);
  };

  const removeCurrentLocalRecipe = () => {
    if (!currentLocalRecipe) return;
    const removedRecipe = currentLocalRecipe;
    const nextLocalRecipes = localRecipes.filter(
      (recipe) => recipe.id !== removedRecipe.id,
    );
    storeLocalRecipes(nextLocalRecipes);
    setLocalRecipes(nextLocalRecipes);
    commitRecipeEdit(withoutLocalRecipeLink);
    appendLog("info", `Local recipe removed: ${removedRecipe.name}.`);
  };

  const setPitchRange = (min: number, max: number) => {
    commitRecipeEdit((current) => withPitchRange(current, min, max));
  };

  const toggleRecipeShape = (enabled: boolean) => {
    const wasUsingRecipe = settings.presetId !== "custom";
    const lastRecipe = recipeHistory.present.lastRecipeId;
    commitRecipeEdit((current) => withRecipeShape(current, enabled));

    if (!enabled && wasUsingRecipe) {
      appendLog("info", "Manual switched to Custom generation.");
    }
    if (enabled && !wasUsingRecipe) {
      appendLog(
        "info",
        `Manual reattached to ${PRESETS.find((preset) => preset.id === lastRecipe)?.name ?? lastRecipe}.`,
      );
    }
  };

  const makeBatchForLocations = (
    locations: Td3Location[],
    batchSettings: ChaosSettings,
    runSeed: string,
  ): BatchItem[] => {
    const patterns = generatePatterns(
      batchSettings,
      locations.length,
      runSeed,
      { locations },
    );
    return locations.map((location, index) => ({
      location,
      pattern: patterns[index],
      sysex: buildPatternSysEx(patterns[index], location),
    }));
  };

  const makeBatch = (runSeed: string): BatchItem[] =>
    makeBatchForLocations(selectedLocations, safeSettings, runSeed);

  const makeDumpMapBatch = (runSeed: string): BatchItem[] => {
    const indexedBatch = new Map<number, BatchItem>();
    dumpMapEntries.forEach((entry, entryIndex) => {
      const locations = LOCATION_OPTIONS.slice(
        entry.startIndex,
        entry.endIndex + 1,
      );
      const regionBatch = makeBatchForLocations(
        locations,
        entry.settings,
        `${runSeed}:map:${entryIndex}:${entry.startIndex}-${entry.endIndex}`,
      );
      regionBatch.forEach((item) => {
        indexedBatch.set(locationToIndex(item.location), item);
      });
    });
    return Array.from(indexedBatch.entries())
      .sort(([left], [right]) => left - right)
      .map(([, item]) => item);
  };

  const makeActiveBatch = (runSeed: string): BatchItem[] =>
    hasDumpMap ? makeDumpMapBatch(runSeed) : makeBatch(runSeed);

  const addDumpMapEntry = () => {
    const startIndex = locationToIndex(selectedLocations[0]);
    const endIndex = locationToIndex(
      selectedLocations[selectedLocations.length - 1],
    );
    const entry: DumpMapEntry = {
      id: nextDumpMapEntryId.current,
      startIndex,
      endIndex,
      recipeName: currentRecipeName,
      settings: normaliseSettings({ ...safeSettings }),
    };
    nextDumpMapEntryId.current += 1;
    setDumpMapEntries((entries) => [...entries, entry]);
    setIsDumpMapOpen(true);
    appendLog(
      "info",
      `Mapped ${locationRangeLabel(startIndex, endIndex)} to ${currentRecipeName}.`,
    );
  };

  const removeDumpMapEntry = (id: number) => {
    setDumpMapEntries((entries) => entries.filter((entry) => entry.id !== id));
  };

  const clearDumpMap = () => {
    setDumpMapEntries([]);
    appendLog("info", "Dump map cleared.");
  };

  const ensureOutput = async (): Promise<MIDIOutput | null> => {
    if (!selectedOutput) {
      appendLog(
        "error",
        "No MIDI output selected. Connect the TD-3 USB MIDI output first.",
      );
      return null;
    }
    if (!midiAccess?.sysexEnabled) {
      appendLog(
        "error",
        "MIDI is not connected with SysEx permission. Reconnect and allow SysEx.",
      );
      return null;
    }
    await selectedOutput.open();
    return selectedOutput;
  };

  const transmitBatch = async (batch: BatchItem[], output: MIDIOutput) => {
    setProgress({
      done: 0,
      total: batch.length,
      action: "Writing...",
    });
    for (let index = 0; index < batch.length; index += 1) {
      if (cancelRequested.current) throw new Error("Cancelled by user.");
      output.send(batch[index].sysex);
      setProgress({
        done: index + 1,
        total: batch.length,
        action: "Writing...",
      });
      appendLog(
        "ok",
        `Wrote ${locationLabel(batch[index].location)} · ${patternSummary(batch[index].pattern)}`,
      );
      await sleep(sendDelayMs);
    }
    setProgress(null);
  };

  const handleJunkMemory = async () => {
    try {
      cancelRequested.current = false;
      const runSeed = seed.trim() || makeEntropySeed();
      const batch = makeActiveBatch(runSeed);
      const confirmed = window.confirm("Overwrite TD-3 pattern data?");
      if (!confirmed) {
        appendLog("info", "Junk write cancelled before sending.");
        return;
      }
      const output = await ensureOutput();
      if (!output) return;
      setLastRunSeed(runSeed);
      setLastBatch(batch);
      appendLog(
        "warn",
        `Starting ${hasDumpMap ? "mapped " : ""}junk write: ${batch.length} TD-3 pattern slot${batch.length === 1 ? "" : "s"} from seed ${runSeed}.`,
      );
      await transmitBatch(batch, output);
      appendLog(
        "ok",
        `Finished. ${batch.length} pattern slot${batch.length === 1 ? "" : "s"} overwritten.`,
      );
    } catch (error) {
      setProgress(null);
      appendLog(
        "error",
        `Write stopped: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  };

  const cancelCurrentJob = () => {
    cancelRequested.current = true;
    if (pendingDump.current) {
      window.clearTimeout(pendingDump.current.timeoutId);
      pendingDump.current.reject(new Error("Cancelled by user."));
      pendingDump.current = null;
    }
    appendLog(
      "warn",
      "Cancel requested. Already-sent SysEx cannot be recalled; remaining slots will be skipped.",
    );
  };

  const downloadBytes = (
    bytes: Uint8Array,
    filename: string,
    type = "application/octet-stream",
  ) => {
    const buffer = new ArrayBuffer(bytes.byteLength);
    new Uint8Array(buffer).set(bytes);
    const blob = new Blob([buffer], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const downloadGeneratedSyx = () => {
    try {
      const runSeed = seed.trim() || makeEntropySeed();
      const batch = makeActiveBatch(runSeed);
      setLastRunSeed(runSeed);
      setLastBatch(batch);
      downloadBytes(
        concatSysex(batch.map((item) => item.sysex)),
        `td3-JunkMem${hasDumpMap ? "-map" : ""}-${batch.length}-patterns.syx`,
      );
      appendLog(
        "ok",
        `Downloaded ${batch.length} ${hasDumpMap ? "mapped " : ""}generated SysEx dump${batch.length === 1 ? "" : "s"} from seed ${runSeed}.`,
      );
    } catch (error) {
      appendLog(
        "error",
        `Generated SysEx failed: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  };

  const waitForDump = (
    expected: Td3Location,
    timeoutMs = 1800,
  ): Promise<PatternDump> => {
    if (pendingDump.current) {
      pendingDump.current.reject(
        new Error("Cancelled by a newer pattern request."),
      );
      window.clearTimeout(pendingDump.current.timeoutId);
      pendingDump.current = null;
    }
    return new Promise((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        pendingDump.current = null;
        reject(new Error(`Timed out waiting for ${locationLabel(expected)}.`));
      }, timeoutMs);
      pendingDump.current = { expected, resolve, reject, timeoutId };
    });
  };

  const requestDump = async (location: Td3Location): Promise<PatternDump> => {
    const output = await ensureOutput();
    if (!output) throw new Error("No MIDI output selected.");
    if (!selectedInput)
      throw new Error("No MIDI input selected for TD-3 responses.");
    await selectedInput.open();
    const pending = waitForDump(location);
    output.send(buildPatternRequestSysEx(location));
    return pending;
  };

  const backupDevice = async () => {
    if (!selectedInput) {
      appendLog(
        "error",
        "No MIDI input selected. Backup needs the TD-3 USB MIDI input/response port.",
      );
      return;
    }
    try {
      cancelRequested.current = false;
      setProgress({
        done: 0,
        total: LOCATION_OPTIONS.length,
        action: "Backing up device patterns",
      });
      const dumps: PatternDump[] = [];
      for (let index = 0; index < LOCATION_OPTIONS.length; index += 1) {
        if (cancelRequested.current) throw new Error("Cancelled by user.");
        const location = LOCATION_OPTIONS[index];
        const dump = await requestDump(location);
        dumps.push(dump);
        setProgress({
          done: index + 1,
          total: LOCATION_OPTIONS.length,
          action: "Backing up device patterns",
        });
        appendLog(
          "ok",
          `Backed up ${locationLabel(location)} · ${patternSummary(dump.pattern)}`,
        );
        await sleep(55);
      }
      setProgress(null);
      downloadBytes(
        concatSysex(dumps.map((dump) => dump.sysex)),
        "td3-device-backup.syx",
      );
      appendLog(
        "ok",
        `Device backup downloaded for ${dumps.length} pattern slots.`,
      );
    } catch (error) {
      setProgress(null);
      appendLog(
        "error",
        `Backup failed: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  };

  const restoreBackupFile = async (file: File) => {
    try {
      cancelRequested.current = false;
      const output = await ensureOutput();
      if (!output) return;

      const bytes = new Uint8Array(await file.arrayBuffer());
      const dumps = decodePatternDumpFile(bytes);
      setProgress({
        done: 0,
        total: dumps.length,
        action: "Restoring backup patterns",
      });
      appendLog(
        "warn",
        `Restoring ${dumps.length} backup pattern slot${dumps.length === 1 ? "" : "s"} from ${file.name}.`,
      );

      for (let index = 0; index < dumps.length; index += 1) {
        if (cancelRequested.current) throw new Error("Cancelled by user.");
        const dump = dumps[index];
        output.send(dump.sysex);
        setProgress({
          done: index + 1,
          total: dumps.length,
          action: "Restoring backup patterns",
        });
        appendLog(
          "ok",
          `Restored ${locationLabel(dump.location)} · ${patternSummary(dump.pattern)}`,
        );
        await sleep(sendDelayMs);
      }

      setProgress(null);
      appendLog(
        "ok",
        `Restore finished. ${dumps.length} TD-3 pattern slot${dumps.length === 1 ? "" : "s"} replaced.`,
      );
    } catch (error) {
      setProgress(null);
      appendLog(
        "error",
        `Restore failed: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  };

  const selectBackupFile = () => {
    restoreBackupInputRef.current?.click();
  };

  const handleBackupFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (!file) return;
    void restoreBackupFile(file);
  };

  return (
    <main className="app-shell">
      <section className="top-panel panel">
        <div className="title-block">
          <h1>
            <button
              className="title-button"
              type="button"
              onClick={() => setIsAboutOpen(true)}
              aria-haspopup="dialog"
              aria-label="About TD-3 JunkMem"
            >
              TD-3 JunkMem
            </button>
          </h1>
        </div>
        <div className="write-card">
          <div className="slot-count">
            <span>{activeSlotCount}</span>
            <small>{activeSlotLabel}</small>
          </div>
          <button
            className="big-red"
            onClick={handleJunkMemory}
            disabled={!selectedOutput || !midiAccess?.sysexEnabled || Boolean(progress)}
          >
            {progress
              ? `${progress.done}/${progress.total}`
              : hasDumpMap
                ? `Junk map ${activeSlotCount}`
                : `Junk ${activeSlotCount}`}
          </button>
          {progress && (
            <button className="secondary cancel-button" onClick={cancelCurrentJob}>
              Cancel
            </button>
          )}
          {progress && (
            <div className="progress-mini" aria-label={progress.action}>
              <span>{progress.action}</span>
              <div>
                <i
                  style={{
                    width: `${(progress.done / progress.total) * 100}%`,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="workspace">
        <aside className="panel setup-panel">
          <div className="panel-heading compact-heading">
            <h2>MIDI</h2>
            <button className="secondary" onClick={connectMidi}>
              {midiAccess ? "Refresh" : "Connect MIDI"}
            </button>
          </div>
          {!isTd3Connected && <p className="hint">Connect TD-3 via USB.</p>}
          <div className="status-row">
            <StatusPill
              ok={isTd3Connected}
              label={isTd3Connected ? "Connected" : "Not connected"}
            />
          </div>
          <label>
            Output to TD-3
            <select
              value={selectedOutputId}
              onChange={(event) => setSelectedOutputId(event.target.value)}
            >
              <option value="">No output selected</option>
              {outputs.map((output) => (
                <option value={output.id} key={output.id}>
                  {portName(output)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Input from TD-3
            <select
              value={selectedInputId}
              onChange={(event) => setSelectedInputId(event.target.value)}
            >
              <option value="">No input selected</option>
              {inputs.map((input) => (
                <option value={input.id} key={input.id}>
                  {portName(input)}
                </option>
              ))}
            </select>
          </label>

          <h3>Memory target</h3>
          <div className="segmented">
            <button
              className={targetMode === "all" ? "active" : ""}
              onClick={() => setTargetMode("all")}
            >
              All 64
            </button>
            <button
              className={targetMode === "range" ? "active" : ""}
              onClick={() => setTargetMode("range")}
            >
              Range
            </button>
            <button
              className={targetMode === "single" ? "active" : ""}
              onClick={() => setTargetMode("single")}
            >
              Single
            </button>
          </div>
          {targetMode === "single" && (
            <LocationSelect
              label="Pattern slot"
              value={singleIndex}
              onChange={setSingleIndex}
            />
          )}
          {targetMode === "range" && (
            <div className="grid compact range-select-grid">
              <LocationSelect
                label="From"
                value={rangeStart}
                onChange={setRangeStart}
              />
              <LocationSelect
                label="To"
                value={rangeEnd}
                onChange={setRangeEnd}
              />
            </div>
          )}
          <div className="dump-map">
            <div className="dump-map-toolbar">
              <button
                className="secondary map-toggle"
                onClick={() => setIsDumpMapOpen((open) => !open)}
                aria-expanded={isDumpMapOpen}
              >
                Dump map: {dumpMapEntries.length}
              </button>
              <button
                className="secondary"
                onClick={addDumpMapEntry}
                disabled={Boolean(progress)}
              >
                Add current range
              </button>
            </div>
            {isDumpMapOpen && (
              <div className="map-list">
                {dumpMapEntries.length === 0 ? (
                  <p className="hint map-empty">No mapped regions yet.</p>
                ) : (
                  dumpMapEntries.map((entry) => (
                    <div className="map-entry" key={entry.id}>
                      <div className="map-entry-main">
                        <strong>
                          {locationRangeLabel(entry.startIndex, entry.endIndex)}
                        </strong>
                        <span>{entry.recipeName}</span>
                      </div>
                      <button
                        className="secondary map-remove"
                        onClick={() => removeDumpMapEntry(entry.id)}
                        disabled={Boolean(progress)}
                      >
                        Remove
                      </button>
                    </div>
                  ))
                )}
                {dumpMapEntries.length > 0 && (
                  <div className="map-footer">
                    <span>
                      {dumpMapStats.slotCount} slot
                      {dumpMapStats.slotCount === 1 ? "" : "s"}
                      {dumpMapStats.overlapCount > 0
                        ? " · latest overlap wins"
                        : ""}
                    </span>
                    <button
                      className="secondary map-remove"
                      onClick={clearDumpMap}
                      disabled={Boolean(progress)}
                    >
                      Clear map
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="button-row">
            <button
              className="secondary"
              onClick={backupDevice}
              disabled={!selectedInput || Boolean(progress)}
            >
              Backup device.syx
            </button>
            <button
              className="secondary"
              onClick={selectBackupFile}
              disabled={
                !selectedOutput || !midiAccess?.sysexEnabled || Boolean(progress)
              }
            >
              Restore backup.syx
            </button>
            <input
              ref={restoreBackupInputRef}
              type="file"
              accept=".syx,application/octet-stream"
              onChange={handleBackupFileSelected}
              hidden
            />
            <button
              className="secondary"
              onClick={downloadGeneratedSyx}
              disabled={Boolean(progress)}
            >
              Download JunkMem.syx
            </button>
          </div>
        </aside>

        <section className="panel main-panel">
          <div className="tabbar-row">
            <div
              className="tabbar"
              role="tablist"
              aria-label="TD-3 JunkMem tools"
            >
              <TabButton
                id="recipes"
                activeTab={activeTab}
                setActiveTab={setActiveTab}
              >
                Recipes
              </TabButton>
              <TabButton
                id="manual"
                activeTab={activeTab}
                setActiveTab={setActiveTab}
              >
                Manual
              </TabButton>
              <TabButton
                id="activity"
                activeTab={activeTab}
                setActiveTab={setActiveTab}
              >
                Activity
              </TabButton>
            </div>
          </div>

          {activeTab === "recipes" && (
            <div className="tab-content">
              <div className="recipe-grid">
                {PRESETS.map((preset) => (
                  <button
                    className={`recipe-card ${
                      !currentLocalRecipe && safeSettings.presetId === preset.id
                        ? "active"
                        : ""
                    }`}
                    key={preset.id}
                    onClick={() => selectPreset(preset.id)}
                    aria-pressed={
                      !currentLocalRecipe && safeSettings.presetId === preset.id
                    }
                  >
                    <strong>{preset.name}</strong>
                  </button>
                ))}
                {localRecipes.map((recipe) => (
                  <button
                    className={`recipe-card local ${
                      currentLocalRecipe?.id === recipe.id ? "active" : ""
                    }`}
                    key={recipe.id}
                    onClick={() => selectLocalRecipe(recipe)}
                    aria-pressed={currentLocalRecipe?.id === recipe.id}
                  >
                    <strong>{recipe.name}</strong>
                    <small>Local</small>
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeTab === "manual" && (
            <div className="tab-content manual-grid">
              <div>
                <div className="panel-heading">
                  <div>
                    <p className="hint manual-current-recipe">
                      Current recipe:{" "}
                      <strong>{currentRecipeName}</strong>.
                    </p>
                  </div>
                  <div className="manual-recipe-actions">
                    <button
                      className="secondary subtle"
                      type="button"
                      onClick={addLocalRecipe}
                    >
                      Add recipe
                    </button>
                    {currentLocalRecipe && (
                      <button
                        className="secondary subtle danger"
                        type="button"
                        onClick={removeCurrentLocalRecipe}
                      >
                        Remove recipe
                      </button>
                    )}
                  </div>
                </div>
                <div className="recipe-shape-controls">
                  <label className="shape-toggle">
                    <input
                      type="checkbox"
                      checked={keepRecipeShape}
                      onChange={(event) =>
                        toggleRecipeShape(event.target.checked)
                      }
                    />{" "}
                    Use recipe shape
                  </label>
                </div>
                <div className="slider-grid">
                  <Slider
                    label="Accent"
                    value={safeSettings.accentChance}
                    onChange={(value) => updateSetting("accentChance", value)}
                  />
                  <Slider
                    label="Slide"
                    value={safeSettings.slideChance}
                    onChange={(value) => updateSetting("slideChance", value)}
                  />
                  <Slider
                    label="Rest"
                    value={safeSettings.restChance}
                    onChange={(value) => updateSetting("restChance", value)}
                  />
                  <Slider
                    label="Tie"
                    value={safeSettings.tieChance}
                    onChange={(value) => updateSetting("tieChance", value)}
                  />
                  <Slider
                    label="Triplet"
                    value={safeSettings.tripletChance}
                    onChange={(value) => updateSetting("tripletChance", value)}
                  />
                </div>
                <div className="check-row">
                  <label>
                    <input
                      type="checkbox"
                      checked={safeSettings.enforceAudibleStep}
                      onChange={(event) =>
                        updateSetting(
                          "enforceAudibleStep",
                          event.target.checked,
                        )
                      }
                    />{" "}
                    Blank guard: force one real note trigger
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={safeSettings.allowTieOnFirstStep}
                      onChange={(event) =>
                        updateSetting(
                          "allowTieOnFirstStep",
                          event.target.checked,
                        )
                      }
                    />{" "}
                    Allow first-step tie weirdness
                  </label>
                </div>
              </div>

              <div>
                <div className="grid two compact">
                  <NumberBox
                    label="Min steps"
                    value={safeSettings.lengthMin}
                    min={TD3_ACTIVE_STEPS_MIN}
                    max={TD3_ACTIVE_STEPS_MAX}
                    onChange={(value) => updateSetting("lengthMin", value)}
                    formatter={(value) =>
                      value === 1 ? "1 active step" : `${value} active steps`
                    }
                  />
                  <NumberBox
                    label="Max steps"
                    value={safeSettings.lengthMax}
                    min={TD3_ACTIVE_STEPS_MIN}
                    max={TD3_ACTIVE_STEPS_MAX}
                    onChange={(value) => updateSetting("lengthMax", value)}
                    formatter={(value) =>
                      value === 1 ? "1 active step" : `${value} active steps`
                    }
                  />
                  <NumberBox
                    label="Pitch min"
                    value={safeSettings.pitchMin}
                    min={TD3_PLAYABLE_PITCH_MIN}
                    max={TD3_PLAYABLE_PITCH_MAX}
                    onChange={(value) => updateSetting("pitchMin", value)}
                    formatter={(value) =>
                      `0x${value.toString(16).toUpperCase().padStart(2, "0")} · ${pitchName(value)}`
                    }
                  />
                  <NumberBox
                    label="Pitch max"
                    value={safeSettings.pitchMax}
                    min={TD3_PLAYABLE_PITCH_MIN}
                    max={TD3_PLAYABLE_PITCH_MAX}
                    onChange={(value) => updateSetting("pitchMax", value)}
                    formatter={(value) =>
                      `0x${value.toString(16).toUpperCase().padStart(2, "0")} · ${pitchName(value)}`
                    }
                  />
                </div>
                <div className="quick-ranges">
                  <button
                    className="secondary"
                    onClick={() => setPitchRange(0x0c, 0x1b)}
                  >
                    Bass
                  </button>
                  <button
                    className="secondary"
                    onClick={() => setPitchRange(0x18, 0x27)}
                  >
                    Mid
                  </button>
                  <button
                    className="secondary"
                    onClick={() => setPitchRange(0x24, TD3_PLAYABLE_PITCH_MAX)}
                  >
                    High
                  </button>
                  <button
                    className="secondary"
                    onClick={() =>
                      setPitchRange(
                        TD3_PLAYABLE_PITCH_MIN,
                        TD3_PLAYABLE_PITCH_MAX,
                      )
                    }
                  >
                    Full
                  </button>
                </div>
                <div className="grid three compact">
                  <label>
                    Scale clamp
                    <select
                      value={safeSettings.scaleId}
                      onChange={(event) =>
                        updateSetting("scaleId", event.target.value as ScaleId)
                      }
                    >
                      <option value="none">None: raw junk</option>
                      <option value="chromatic">Chromatic</option>
                      <option value="minor-pentatonic">Minor pentatonic</option>
                      <option value="natural-minor">Natural minor</option>
                      <option value="dorian">Dorian</option>
                      <option value="phrygian">Phrygian</option>
                      <option value="acid-blues">Acid blues</option>
                    </select>
                  </label>
                  <label>
                    Root
                    <select
                      value={safeSettings.root}
                      onChange={(event) =>
                        updateSetting("root", Number(event.target.value))
                      }
                      disabled={safeSettings.scaleId === "none"}
                    >
                      {ROOT_NAMES.map((root, index) => (
                        <option value={index} key={root}>
                          {root}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Seed
                    <input
                      value={seed}
                      onChange={(event) => setSeed(event.target.value)}
                      placeholder="blank = new chaos"
                    />
                  </label>
                </div>
                <details className="advanced">
                  <summary>Write pacing</summary>
                  <div className="grid compact">
                    <NumberBox
                      label="Delay between writes"
                      value={sendDelayMs}
                      min={20}
                      max={500}
                      onChange={setSendDelayMs}
                      suffix="ms"
                    />
                  </div>
                </details>
              </div>
            </div>
          )}

          {activeTab === "activity" && (
            <div className="tab-content activity-grid">
              <div>
                <div className="panel-heading">
                  <h2>Activity</h2>
                  <button className="secondary" onClick={() => setLogs([])}>
                    Clear
                  </button>
                </div>
                <div className="log-list">
                  {logs.length === 0 && (
                    <p className="hint">No activity yet.</p>
                  )}
                  {logs.map((entry) => (
                    <p className={`log ${entry.level}`} key={entry.id}>
                      {entry.text}
                    </p>
                  ))}
                </div>
              </div>
              <div>
                <h2>Last batch</h2>
                {lastBatch.length === 0 ? (
                  <p className="hint">Nothing generated yet.</p>
                ) : (
                  <>
                    <p className="summary">
                      Seed: <code>{lastRunSeed}</code>
                    </p>
                    <div className="mini-list">
                      {lastBatch.slice(0, 32).map((item) => (
                        <p key={locationLabel(item.location)}>
                          <strong>{locationLabel(item.location)}</strong> ·{" "}
                          {patternSummary(item.pattern)}
                        </p>
                      ))}
                      {lastBatch.length > 32 && (
                        <p>…and {lastBatch.length - 32} more.</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </section>
      </section>
      {isAboutOpen && <AboutModal onClose={() => setIsAboutOpen(false)} />}
    </main>
  );
}

function portName(port: MIDIPort): string {
  const bits = [port.name, port.manufacturer, port.version].filter(Boolean);
  return bits.join(" · ") || port.id;
}

function StatusPill({ ok, label }: { ok: boolean; label: string }) {
  return <span className={`status-pill ${ok ? "ok" : "not-ok"}`}>{label}</span>;
}

function AboutModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="app-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="about-modal-title"
    >
      <button
        className="modal-backdrop"
        type="button"
        onClick={onClose}
        aria-label="Close about dialog"
      />
      <div className="modal-dialog">
        <div className="modal-header">
          <h2 id="about-modal-title">About TD-3 JunkMem</h2>
          <button
            className="modal-close"
            type="button"
            onClick={onClose}
            aria-label="Close"
          >
            x
          </button>
        </div>
        <div className="modal-body">
          <p>
            TD-3 JunkMem is a browser-based WebMIDI utility to emulate
            corrupted pattern memory data after removing the TB-303's
            batteries.
          </p>
          <p>Created by WaftSoft, 2026.</p>
          <div className="license-note">
            <strong>
              © WaftSoft 2026. Personal use only. Redistribution, resale, or
              rebranding without permission is not permitted.
            </strong>
          </div>
          <ul className="modal-notes">
            <li>No affiliation with Behringer; trademarks belong to their owners.</li>
            <li>Back up your TD-3 before overwriting pattern memory.</li>
          </ul>
          <div className="about-support" aria-label="Support WaftSoft">
            <p className="about-support-copy">support if you can and want to</p>
            <iframe
              id="kofiframe"
              className="kofi-frame"
              src="https://ko-fi.com/waftsoft/?hidefeed=true&widget=true&embed=true"
              height="520"
              loading="lazy"
              title="Support WaftSoft on Ko-fi"
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="secondary modal-ok" type="button" onClick={onClose}>
            Nice
          </button>
        </div>
      </div>
    </div>
  );
}

function TabButton({
  id,
  activeTab,
  setActiveTab,
  children,
}: {
  id: TabId;
  activeTab: TabId;
  setActiveTab: (id: TabId) => void;
  children: ReactNode;
}) {
  return (
    <button
      className={activeTab === id ? "active" : ""}
      onClick={() => setActiveTab(id)}
      role="tab"
      aria-selected={activeTab === id}
    >
      {children}
    </button>
  );
}

function LocationSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  const safeValue = clampLocationIndex(value);
  return (
    <label>
      {label}
      <select
        value={safeValue}
        onChange={(event) =>
          onChange(clampLocationIndex(Number(event.target.value)))
        }
      >
        {LOCATION_OPTIONS.map((location, index) => (
          <option value={index} key={locationLabel(location)}>
            {locationLabel(location)}
          </option>
        ))}
      </select>
    </label>
  );
}

function Slider({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  const safeValue = Math.max(
    0,
    Math.min(1, Number.isFinite(value) ? value : 0),
  );
  return (
    <label className="slider-label">
      <span>
        {label}
        <strong>{Math.round(safeValue * 100)}%</strong>
      </span>
      <input
        type="range"
        min={0}
        max={1}
        step={0.01}
        value={safeValue}
        onChange={(event) =>
          onChange(Math.max(0, Math.min(1, Number(event.target.value))))
        }
      />
    </label>
  );
}

function NumberBox({
  label,
  value,
  min,
  max,
  onChange,
  formatter,
  suffix,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  formatter?: (value: number) => string;
  suffix?: string;
}) {
  const safeValue = Math.max(
    min,
    Math.min(max, Math.round(Number.isFinite(value) ? value : min)),
  );
  const parse = (raw: string) => {
    if (raw.trim() === "") return;
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) return;
    onChange(Math.max(min, Math.min(max, Math.round(numeric))));
  };
  return (
    <label>
      {label}
      <input
        type="number"
        min={min}
        max={max}
        value={safeValue}
        onChange={(event) => parse(event.target.value)}
      />
      <small>
        {formatter
          ? formatter(safeValue)
          : suffix
            ? `${safeValue}${suffix}`
            : ""}
      </small>
    </label>
  );
}

export default App;
